import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../../core/services/bx-unverified-alert.service';
import { DialogParams, DialogService } from '../../../../../core/services/dialog.service';
import { InvoiceSharingService } from '../../../../../core/services/invoice-sharing.service';
import { LaunchDarklyService } from '../../../../../core/services/launchdarkly.service';
import { FeatureService } from '../../../../../core/services/permission/feature.service';
import { PermissionService } from '../../../../../core/services/permission/permission.service';
import { Job, JobPayment } from '../../../../../data/models';
import { RepoJobsService } from '../../../../../data/repositories/repo-jobs.service';
import { SharedDataService } from '../../../../../data/services/shared-data.service';
import { InvoicesReceiptService } from '../../../invoices/invoices-receipt.service';
import { InvoicesService } from '../../../invoices/invoices.service';
import { CustomLayoutsService } from './../../../../estimates/custom-layouts/custom-layouts.service';

@Component({
    selector: 'app-job-invoices-retention',
    templateUrl: './job-invoices-retention.component.html',
    standalone: false,
})
export class JobInvoicesRetentionComponent implements OnInit {
    // @ts-ignore TS2564
    @Input() job: Job;
    @Output() invoiceChange = new EventEmitter();
    rows: JobPayment[] = [];
    tableHeight = 'auto';
    lastSharedInvoiceStatus = '';
    generatingPDF = false;
    pdfUrl = '';
    translations = {
        variations: '',
        retentionTotalTooltip: '',
        variationsExTitle: '',
    };

    constructor(
        public featureService: FeatureService,
        public permissionService: PermissionService,
        private invoiceSharingService: InvoiceSharingService,
        private logger: BxLogger,
        private bxUnverifiedAlertService: BxUnverifiedAlertService,
        private translateService: TranslateService,
        private invoicesService: InvoicesService,
        private invoicesReceiptService: InvoicesReceiptService,
        private repoJobs: RepoJobsService,
        private customLayoutsService: CustomLayoutsService,
        private sharedData: SharedDataService,
        public lds: LaunchDarklyService,
        private dialogService: DialogService
    ) {}

    ngOnInit(): void {
        const text = this.translateService.instant('general.variations');
        this.translations.variations = text.toLowerCase();
        this.translations.retentionTotalTooltip = `${this.job.RetentionPercentage}% of the contract total (${
            this.job.RetentionType === 0 ? 'excl' : 'incl'
        } ${this.translations.variations})`;
        this.translations.variationsExTitle = `${text} (Ex)`;
        this.load();
    }

    load() {
        // @ts-ignore TS2532
        this.rows = this.job.JobPayments.filter((p) => {
            return !p.IsDeleted && p.IsRetentionInvoice && !p.IsDepositInvoice;
        });
        // this.tableHeight = this.rows.length * 42 + 52 + 52 + 'px';
    }

    addRetentionInvoice() {
        // Cannot add invoices if one is pending
        if (
            // @ts-ignore TS2532
            this.job.JobPayments.some((i) => {
                return !i.IsDeleted && i.Status === 'Pending' && !i.IsDepositInvoice;
            })
        ) {
            this.dialogService.show({
                type: 'info',
                title: 'Cannot add another invoice',
                text: 'You already have a pending invoice, please mark that as invoiced first before creating another one.',
                showCancel: false,
            });
            return;
        }

        this.invoicesService
            .newInvoice(this.job, {
                IsRetentionInvoice: true,
            })
            .then(
                () => {
                    this.load();
                    this.invoiceChange.emit();
                },
                () => ({})
            );
    }

    changeRetentionType() {
        if (this.job.RetentionType !== 0) return;
        const params: DialogParams = {
            title: 'You cannot undo this change',
            text: 'Please confirm you want to change the retention total to include variations.',
            type: 'warning',
        };
        this.dialogService.show(params).then(
            () => {
                this.job.RetentionType = 1;
                this.repoJobs.Save(this.job, 'Retention Total updated');
            },
            () => {}
        );
    }

    viewInvoice(invoice: JobPayment) {
        this.invoicesService.viewInvoice(invoice).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            (error) => ({})
        );
    }

    deleteInvoice(evt: MouseEvent, invoice: JobPayment) {
        evt.stopPropagation();
        const params = {
            title: 'Delete invoice?',
            text: "If you go ahead, you won't be able to get it back.",
        };
        this.dialogService.show(params).then(
            () => {
                invoice.IsDeleted = true;
                this.repoJobs.Save(null, 'Invoice has been deleted');
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    printInvoice(invoice: JobPayment) {
        this.invoicesService.generateInvoice(invoice, undefined, true);
    }

    shareInvoice(invoice: JobPayment) {
        this.lastSharedInvoiceStatus = invoice.Status;
        const documentSource = { Id: this.job.ID_Job, Type: 'Job' };
        this.invoiceSharingService.getEmailConfig(invoice, 'pdf').then((emailConfig) => {
            const shareConfig = this.invoiceSharingService.getShareConfig(invoice);
            this.customLayoutsService.openDigitalAcceptanceShareForm(
                emailConfig,
                shareConfig,
                null,
                true,
                3,
                false,
                this.job.ID_Lead,
                documentSource,
                true,
                invoice,
                () => {
                    this.handleShareSuccess();
                },
                () => {
                    this.resetInvoiceStatus(invoice);
                }
            ); // 3 : DocumentTypes.Invoice
        });
    }

    handleShareSuccess() {
        this.logger.success('Invoice Shared');
    }

    resetInvoiceStatus(invoice: JobPayment) {
        if (this.lastSharedInvoiceStatus !== invoice.Status) {
            invoice.Status = this.lastSharedInvoiceStatus;
            this.invoicesService.setInvoiceNumber(invoice).then((invoiceWithId) => {
                this.invoicesService.saveInvoice(invoiceWithId);
                this.invoiceChange.emit();
            });
        }
    }

    // TODO:: move to shared component to slim the product
    emailInvoice(invoice: JobPayment) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.invoicesService.emailInvoice(invoice).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    addReceipt(invoice: JobPayment) {
        this.invoicesReceiptService.addReceipt(invoice).then(
            () => {
                this.repoJobs.Save(null, 'Receipt saved');
                this.invoiceChange.emit();
            },
            () => {
                invoice.entityAspect.rejectChanges();
            }
        );
    }
}
